import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { AIChatProvider } from "@/Chat/ChatSidebar/AIChatContext";
import { Chat } from "@/Chat/ChatSidebar/Chat";
import { ChatByIdView } from "@/Chat/ChatSidebar/ChatByIdView";
import { useAuth } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { Button } from "@/shared/foundation/primitives";

import styles from "./AdminChatViewer.module.css";
import { CopyLinkButton } from "./CopyLinkButton";
import { ReadOnlyAIChatProvider } from "./ReadOnlyAIChatProvider";

interface AdminChatViewerProps {
  chatId: string;
  onBack: () => void;
  /** Sequence number of the message to scroll to once the chat renders. */
  scrollToMessageSequenceNum?: number;
  onViewFeedbackForChat?: () => void;
  /** Invoked when an embedded child of the viewer switches to a different
   *  chat. Fires from the interactive branch when the admin picks a chat from
   *  the history pane, and from the read-only branch when a fork succeeds
   *  (navigating the viewer to the admin's freshly created copy). */
  onChatSelected?: (chatId: string) => void;
  /** Fully-qualified URL to copy when the admin hits the header share
   *  button. Parent decides whether this points at the chat or at a
   *  specific feedback entry so the link matches the context the admin is
   *  viewing in. */
  shareUrl: string;
}

export function AdminChatViewer({
  chatId,
  onBack,
  scrollToMessageSequenceNum,
  onViewFeedbackForChat,
  onChatSelected,
  shareUrl,
}: AdminChatViewerProps) {
  const { chatService } = useDomainServices();
  const { currentUser } = useAuth();
  const {
    data: chat,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["adminChat", chatId],
    queryFn: ({ signal }) =>
      chatService.getChatById(chatId, true, { abortSignal: signal }),
    enabled: !!chatId,
  });
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    containerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [chatId]);

  // Scroll to a specific message once the chat is loaded. We query by
  // ``data-message-sequence-num`` rather than by article position because
  // tool groups, pending tool uses, and approval cards also render as
  // ``<article>`` elements — indexing by position would land on the wrong
  // element. ``requestAnimationFrame`` defers the lookup until after React
  // has committed the DOM (the previous setTimeout(100) was a magic-number
  // race against the same render cycle).
  React.useEffect(() => {
    if (!chat || scrollToMessageSequenceNum === undefined) {
      return;
    }
    const handle = window.requestAnimationFrame(() => {
      const target = containerRef.current?.querySelector(
        `[data-message-sequence-num="${scrollToMessageSequenceNum}"]`,
      );
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(handle);
  }, [chat, scrollToMessageSequenceNum]);

  // Memoised so passing the prop doesn't recreate the function each render —
  // the AIChatProvider effect that watches ``onChatIdChanged`` would
  // otherwise re-run every render and treat each as a fresh handler.
  const handleChatIdChangedFromInteractive = React.useCallback(
    (nextId: string | undefined) => {
      if (nextId && nextId !== chat?.session_id) {
        onChatSelected?.(nextId);
      }
    },
    [chat?.session_id, onChatSelected],
  );

  // ``currentUser`` is ``null`` while auth is loading (never ``undefined``).
  // Treat the branch decision as "unresolved" until both the chat and the
  // user are loaded — the previous code briefly flashed the read-only
  // viewer and banner on the admin's own chat until auth resolved.
  const ownerCheckResolved = chat !== undefined && currentUser !== null;
  const isOwnChat =
    ownerCheckResolved && chat.created_by === currentUser.user_id;

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.header}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
        <CopyLinkButton
          url={shareUrl}
          tooltip="Copy a share link to this view"
        />
        {onViewFeedbackForChat && (
          <Button
            size="small"
            variant="outlined"
            onClick={onViewFeedbackForChat}
          >
            View feedback for this chat
          </Button>
        )}
        {chat && (
          <div className={styles.metadata}>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Org:</span>
              <span className={styles.metadataValue}>{chat.org_id}</span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>User:</span>
              <span className={styles.metadataValue}>{chat.created_by}</span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Created:</span>
              <span className={styles.metadataValue}>
                {new Date(chat.created).toLocaleString()}
              </span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Model:</span>
              <span className={styles.metadataValue}>
                {chat.model_profile || "default"}
              </span>
            </div>
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Messages:</span>
              <span className={styles.metadataValue}>
                {chat.messages.length}
              </span>
            </div>
            {chat.forked_from_session_id && (
              <div className={styles.metadataItem}>
                <span className={styles.metadataLabel}>Forked from:</span>
                <span className={styles.metadataValue}>
                  {chat.forked_from_session_id}
                  {chat.forked_from_message_sequence_num !== null &&
                  chat.forked_from_message_sequence_num !== undefined
                    ? ` @ msg ${chat.forked_from_message_sequence_num}`
                    : ""}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.chatContent}>
        {isLoading && (
          <div className={styles.loadingContainer}>
            <CircularProgress size="1.5rem" />
          </div>
        )}
        {error && (
          <div className={styles.errorContainer}>
            Failed to load chat: {error.message}
          </div>
        )}
        {chat && ownerCheckResolved && isOwnChat && (
          // Admin's own chat — render the full interactive Chat so they can
          // continue the conversation in place. ``key`` forces a fresh provider
          // when they jump to a different own-chat so internal state resets.
          <AIChatProvider
            key={chat.session_id}
            initialChatId={chat.session_id}
            onChatIdChanged={handleChatIdChangedFromInteractive}
          >
            <Chat />
          </AIChatProvider>
        )}
        {chat && ownerCheckResolved && !isOwnChat && (
          // Read-only branch: forks navigate the viewer to the admin's new
          // copy via ``onChatClicked``. Without this, ``ForkChatButton``'s
          // success path lands on a no-op and the admin sees no change.
          <ReadOnlyAIChatProvider onChatClicked={onChatSelected}>
            <ChatByIdView chat={chat} />
          </ReadOnlyAIChatProvider>
        )}
      </div>

      {chat && ownerCheckResolved && !isOwnChat && (
        <div className={styles.readOnlyBanner}>
          Read-only view. Fork from any assistant message to continue the
          conversation in your own session.
        </div>
      )}
    </div>
  );
}
